'use client';

import { useState } from 'react';
import { useSwrRecruiterCallQuote } from '@hooks/useSwrApi/endpoints/recruiter-call';

interface UseRecruiterCallEntryProps {
  candidateUserId?: number | null;
  /**
   * Slug alternative to `candidateUserId`. Used by card contexts
   * (applicant / pipeline flow) where the DTO carries a slug but no
   * numeric user id. Backend accepts either; when only the slug is sent,
   * the resolved user id is echoed back on the response so the softphone
   * flow (which needs a numeric id) still works.
   */
  candidateSlug?: string | null;
  jobSlug?: string | null;
  /**
   * Gate the quote SWR call. Card-level callers pass `hovered` so the
   * request only fires when the user actually reveals the actions row —
   * avoids N quote requests per infinite-scroll page.
   */
  shouldFetch?: boolean;
}

/**
 * @description Shared entry-point hook for the RecruiterCall button, used by
 * both the CV-detail header Contact button (`UnblockActions`) and the applicant-card
 * variant (`CallCandidateCardAction`).
 *
 * Wraps the `/v3/recruiter-calls/quote` fetch + the softphone-modal
 * open/close state. Callers receive:
 *   - `visible`  — false when `canCall === false` OR the quote hasn't loaded
 *                  yet; both variants render nothing in that case.
 *   - `priceCredits` / `remainingFreeCalls` — the two numbers the button
 *                  badges consume.
 *   - `resolvedCandidateUserId` — echoed by the backend so slug-only
 *                  callers have a numeric id to feed the softphone modal.
 *   - `openModal` / `isModalOpen` / `closeModal` — softphone-modal glue.
 */
export const useRecruiterCallEntry = (props: UseRecruiterCallEntryProps) => {
  const {
    candidateUserId, candidateSlug, jobSlug, shouldFetch = true,
  } = props;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data, isLoading } = useSwrRecruiterCallQuote({
    candidateUserId,
    candidateSlug,
    jobSlug,
    shouldFetch,
  });

  const visible = !isLoading && Boolean(data?.canCall);
  const priceCredits = data?.priceCredits ?? 0;
  const remainingFreeCalls = typeof data?.remainingFreeCalls === 'number'
    ? data.remainingFreeCalls
    : null;
  const resolvedCandidateUserId = data?.candidateUserId ?? candidateUserId ?? null;
  // Default to `true` when the flag hasn't loaded yet — the onboarding
  // block is a first-time-only affordance, and a brief "we don't know
  // yet, assume they've been here before" is much less jarring than
  // flashing the onboarding for a returning recruiter while the quote
  // is still in flight.
  const recruiterHasHistory = data?.recruiterHasHistory ?? true;

  return {
    visible,
    isLoading,
    priceCredits,
    remainingFreeCalls,
    resolvedCandidateUserId,
    recruiterHasHistory,
    isModalOpen,
    openModal: () => setIsModalOpen(true),
    closeModal: () => setIsModalOpen(false),
  };
};
